import {
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Cloud,
  Code,
  Cpu,
  Database,
  Eye,
  FileText,
  GitBranch,
  Key,
  LayersIcon,
  Link,
  Lock,
  Network,
  Server,
  Shield,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { motion } from 'motion/react';

import { Button } from '@src/components/ui/button';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

const BOSSS_BLUE = '#2973a3';

// Multi-tenancy architecture SVG diagram
function MultiTenantDiagram() {
  return (
    <div className="bg-slate-900 rounded-2xl p-6 border border-slate-700">
      <div className="text-center mb-4">
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Schema-Based Multi-Tenant Isolation
        </span>
      </div>

      <svg viewBox="0 0 500 260" className="w-full h-auto">
        {/* Shared Public Schema */}
        <rect x="10" y="10" rx="8" width="480" height="60" fill="#1e293b" stroke="#334155" strokeWidth="1.5" />

        <text y="32" x="250" fontSize="10" fill="#94a3b8" fontWeight="600" textAnchor="middle">
          PUBLIC SCHEMA — Subscription Routing Layer
        </text>

        <text y="50" x="250" fontSize="8" fill="#64748b" textAnchor="middle">
          Subscription • SubscriptionDomain • Customer — no tenant data stored here
        </text>

        {/* Arrow down */}
        <path stroke="#475569" strokeWidth="1.5" strokeDasharray="4,3" d="M 250 72 L 250 100" />
        <polygon fill="#475569" points="246,97 254,97 250,104" />

        {/* Tenant schemas row */}
        {[
          { x: 30, label: 'Tenant A', color: '#1e638d' },
          { x: 185, label: 'Tenant B', color: '#7c3aed' },
          { x: 340, label: 'Tenant N', color: '#059669' },
        ].map((tenant) => (
          <g key={tenant.label}>
            <rect
              rx="8"
              y="108"
              width="130"
              x={tenant.x}
              height="130"
              strokeWidth="1.5"
              fill={`${tenant.color}12`}
              stroke={`${tenant.color}60`}
            />

            <text
              y="126"
              fontSize="9"
              fontWeight="700"
              x={tenant.x + 65}
              textAnchor="middle"
              fill={tenant.color}
            >
              {tenant.label}
            </text>

            {[
              { label: 'GRC / Risk', y: 140 },
              { label: 'IAM / People', y: 158 },
              { label: 'CMDB / Assets', y: 176 },
              { label: 'XDR / Alerts', y: 194 },
              { label: 'Blockchain Ledger', y: 212 },
            ].map((row) => (
              <g key={row.label}>
                <rect
                  rx="3"
                  width="114"
                  height="14"
                  y={row.y - 8}
                  x={tenant.x + 8}
                  fill={`${tenant.color}18`}
                />

                <text
                  fontSize="7"
                  y={row.y + 2}
                  fill="#cbd5e1"
                  x={tenant.x + 65}
                  textAnchor="middle"
                >
                  {row.label}
                </text>
              </g>
            ))}
          </g>
        ))}

        {/* No-cross arrows between tenants */}
        <text x="170" y="175" fontSize="16" fill="#ef4444" fontWeight="bold" textAnchor="middle">✕</text>
        <text x="325" y="175" fontSize="16" fill="#ef4444" fontWeight="bold" textAnchor="middle">✕</text>

        <text x="250" y="255" fontSize="8" fill="#64748b" textAnchor="middle">
          Zero cross-tenant data access — enforced at PostgreSQL schema level
        </text>
      </svg>
    </div>
  );
}

const encryptionSpecs = [
  { layer: 'Data in Transit', mechanism: 'TLS 1.3', detail: 'ECDHE/CHACHA20 ciphers, HSTS enforced, forward secrecy mandatory' },
  { layer: 'Data at Rest', mechanism: 'AES-256', detail: 'Database storage, file storage, and backup archives' },
  { layer: 'Field-Level Encryption', mechanism: 'AES-256 + Key Vault', detail: 'Sensitive fields (secrets, credentials, PII) encrypted individually' },
  { layer: 'Key Management', mechanism: 'HashiCorp Vault + Azure Key Vault', detail: 'Production secrets managed in dedicated vault infrastructure; BYOK supported' },
  { layer: 'Password Hashing', mechanism: 'Argon2id', detail: 'Industry-strongest password hashing algorithm with per-user salting' },
  { layer: 'Certificate Management', mechanism: 'DNSSEC + PKIX', detail: 'DNSSEC-enabled domains; TLS certificates auto-renewed with private-only connectivity' },
];

const controlsMatrix = [
  { control: 'Access Control (AC)', implementation: 'Per-tenant DB schemas, RBAC/ABAC, mandatory MFA, session TTL', nist: 'AC-2, AC-3', soc2: 'CC6.1', iso: 'A.9', hipaa: '164.312(a)', gdpr: 'Art. 32' },
  {
    control: 'Audit & Accountability (AU)',
    implementation: 'HistoricalBaseModel on all 30+ domains; SIEM logging; blockchain ledger on People, Identity, Risk',
    nist: 'AU-2, AU-9',
    soc2: 'CC7.2',
    iso: 'A.12.4',
    hipaa: '164.312(b)',
    gdpr: 'Art. 5(f)',
  },
  { control: 'System & Comms Protection (SC)', implementation: 'TLS 1.3, AES-256, field-level encryption via Key Vault, HSTS', nist: 'SC-8, SC-28', soc2: 'CC6.7', iso: 'A.10, A.13', hipaa: '164.312(e)', gdpr: 'Art. 32' },
  {
    control: 'System & Info Integrity (SI)',
    implementation: 'Input validation, rate limiting, SSRF two-tier trust model, ClamAV antivirus on uploads',
    nist: 'SI-10, SI-3',
    soc2: 'CC7.1',
    iso: 'A.12.2',
    hipaa: '164.312(c)',
    gdpr: 'Art. 25',
  },
  { control: 'Contingency Planning (CP)', implementation: 'Azure immutable backups (effective ransomware defense), BCP/DR module, RTO/RPO tracking', nist: 'CP-2, CP-9', soc2: 'CC9.1', iso: 'A.17', hipaa: '164.308(a)(7)', gdpr: 'Art. 32' },
  { control: 'Config Management (CM)', implementation: 'Infrastructure-as-Code (Docker Compose/Terraform), SBOM scanning, dependency pinning', nist: 'CM-3, CM-7', soc2: 'CC8.1', iso: 'A.12.1', hipaa: '164.308(a)(1)', gdpr: 'Art. 25' },
  { control: 'Identification & Auth (IA)', implementation: 'JWT with short TTL, Argon2id passwords, MFA enforced for all roles, SCIM provisioning', nist: 'IA-2, IA-5', soc2: 'CC6.1', iso: 'A.9.4', hipaa: '164.312(d)', gdpr: 'Art. 32' },
  { control: 'Supply Chain Security', implementation: 'Trivy, Bandit, pip-audit, Gitleaks, CodeQL in CI/CD; no unapproved dependencies', nist: 'SR-3, SR-11', soc2: 'CC8.1', iso: 'A.15', hipaa: '164.308(a)(1)', gdpr: 'Art. 32' },
];

const devSecOpsItems = [
  { icon: Code, tool: 'Bandit', purpose: 'Python SAST — finds security anti-patterns and unsafe calls in source code' },
  { icon: Shield, tool: 'Trivy', purpose: 'Container & dependency vulnerability scanning — blocks known CVEs from reaching production' },
  { icon: Key, tool: 'Gitleaks', purpose: 'Secret detection in commits — prevents API keys, tokens, and credentials from entering the repository' },
  { icon: Link, tool: 'pip-audit', purpose: 'Python package vulnerability audit — cross-references PyPI packages against advisory databases' },
  { icon: GitBranch, tool: 'CodeQL', purpose: 'Semantic code analysis — identifies complex vulnerability patterns like injection and XSS' },
  { icon: AlertTriangle, tool: 'Ruff', purpose: 'Fast Python linter catching code quality issues that can introduce security regressions' },
];

const securityLayers = [
  {
    icon: Cloud,
    title: 'Cloud & Infrastructure',
    controls: [
      'Multi-factor authentication with conditional access policies',
      'Privileged identity management for all administrative access',
      'Cloud security posture management and continuous attack surface monitoring',
      'Zone-redundant database with private-only connectivity',
      'Encrypted storage with zero public access endpoints',
    ],
  },
  {
    icon: Network,
    title: 'Network Security',
    controls: [
      'Enterprise-grade reverse proxy with WAF protection',
      'Network segmentation with separate security zones',
      'DNSSEC-enabled domains with managed TLS certificates',
      'Traefik with rate limiting, CSP headers, and proxy-disclosure hardening',
      'Traffic restricted to verified sources only',
    ],
  },
  {
    icon: Server,
    title: 'Infrastructure Security',
    controls: [
      'Three-tier architecture (Web → Application → Database) with strict layer separation',
      'Endpoint detection and response (EDR) on all production systems',
      'Immutable Azure backups — effective ransomware defense with no admin override',
      'Network intrusion detection across all infrastructure layers',
      'Container image hardening — non-root execution enforced on all services',
    ],
  },
  {
    icon: Code,
    title: 'Application & DevSecOps',
    controls: [
      'Single sign-on with mandatory MFA for all user roles',
      'Role-based access control with entity ownership enforcement',
      'Secrets managed via HashiCorp Vault (production) and Azure Key Vault',
      'Automated security scanning in every CI/CD pipeline run',
      'Real-time antivirus on all document and file uploads',
      'SSRF two-tier trust model protecting AI and integration endpoints',
    ],
  },
];

export function SecurityPage() {
  const onNavigateToTop = useNavigateToTop();

  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <section className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}>
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-px bg-blue-400"
              animate={{ opacity: [0.2, 0.6, 0.2] }}
              style={{ left: `${10 + i * 12}%`, height: '100%' }}
              transition={{ duration: 3 + i * 0.4, repeat: Infinity, ease: 'easeInOut', delay: i * 0.3 }}
            />
          ))}
        </div>

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          initial={{ opacity: 0, y: 40 }}
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10"
        >
          <div className="inline-flex items-center bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-blue-500/30">
            <ShieldCheck className="w-4 h-4 mr-2" />
            Security Architecture & Trust
          </div>

          <h1 className="text-5xl lg:text-6xl font-extrabold text-white mb-6">
            Security
            {' '}
            <span className="text-blue-400">By Design</span>
          </h1>

          <p className="max-w-3xl mx-auto text-xl text-slate-300 mb-10 leading-relaxed">
            BOSSS is built with defense-in-depth from the architecture up. Every layer — infrastructure,
            network, application, and data — is independently hardened. Tenant data is cryptographically
            isolated at the PostgreSQL schema level. Every action is permanently recorded.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              className="px-8 py-4 text-lg font-semibold rounded-xl text-gray-900"
              style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
              onClick={() => onNavigateToTop('/demo')}
            >
              Request Architecture Walkthrough
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            <Button
              variant="outline"
              className="px-8 py-4 text-lg font-semibold rounded-xl border-2 border-slate-500 text-slate-200 bg-slate-800/50 hover:bg-slate-700/50 backdrop-blur-sm"
              onClick={() => onNavigateToTop('/contact')}
            >
              Contact Security Team
            </Button>
          </div>
        </motion.div>
      </section>

      {/* Multi-Tenant Isolation Architecture */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-blue-500/30">
              <LayersIcon className="w-4 h-4 mr-2" />
              Multi-Tenant Isolation
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Schema-Level Data Isolation
            </h2>

            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              BOSSS uses PostgreSQL schema-per-tenant isolation — not row-level filtering.
              Each customer&apos;s data lives in a completely separate database schema.
              No query from one tenant can access another tenant&apos;s schema, enforced at the database engine level.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <MultiTenantDiagram />
            </motion.div>

            <motion.div
              className="space-y-5"
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              {[
                {
                  icon: Database,
                  title: 'PostgreSQL Schema Isolation',
                  desc: 'Each tenant is a fully separate PostgreSQL schema. The database engine itself, not application code, enforces the boundary.',
                },
                {
                  icon: ShieldCheck,
                  title: 'Evaluation-Inside-Context Pattern',
                  desc: 'All querysets are evaluated inside the tenant\'s schema context. Lazy queries that might drift to the public schema are caught and rejected by CI tests.',
                },
                {
                  icon: Users,
                  title: 'Tenant-Scoped Background Jobs',
                  desc: 'Celery workers run all background tasks within an explicitly declared schema context — no background job can accidentally read cross-tenant data.',
                },
                {
                  icon: Lock,
                  title: 'Module-Based Access Control',
                  desc: 'Each module (GRC, IAM, CMDB, etc.) must be explicitly activated per tenant. Unactivated modules return 404 — not empty data.',
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex items-start gap-4 p-4 bg-slate-800/60 rounded-xl border border-slate-700"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-blue-400" />
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                    <p className="text-sm text-slate-400">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Encryption Specification */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div
              style={{ backgroundColor: `${BOSSS_BLUE}12`, color: BOSSS_BLUE }}
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <Key className="w-4 h-4 mr-2" />
              Encryption Specification
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Data Protection at Every Layer
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Encryption is applied at rest, in transit, and at the individual field level — not just at the database boundary.
            </p>
          </motion.div>

          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm"
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Layer</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Mechanism</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Implementation Detail</th>
                </tr>
              </thead>

              <tbody>
                {encryptionSpecs.map((spec, i) => (
                  <tr
                    key={i}
                    className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                  >
                    <td className="py-4 px-6">
                      <span className="font-semibold text-gray-900">{spec.layer}</span>
                    </td>

                    <td className="py-4 px-6">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: `${BOSSS_BLUE}12`, color: BOSSS_BLUE }}
                      >
                        {spec.mechanism}
                      </span>
                    </td>

                    <td className="py-4 px-6 text-gray-600">{spec.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Security Controls Matrix */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6 bg-green-100 text-green-800 border border-green-200">
              <FileText className="w-4 h-4 mr-2" />
              Regulatory Alignment
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Security Controls Implementation Matrix
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Platform controls mapped to NIST 800-53, SOC 2, ISO 27001, HIPAA, and GDPR requirements.
            </p>
          </motion.div>

          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm"
          >
            <table className="w-full text-xs min-w-[900px]">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="text-left py-4 px-4 font-semibold">Control Domain</th>
                  <th className="text-left py-4 px-4 font-semibold">BOSSS Implementation</th>
                  <th className="text-center py-4 px-3 font-semibold">NIST 800-53</th>
                  <th className="text-center py-4 px-3 font-semibold">SOC 2</th>
                  <th className="text-center py-4 px-3 font-semibold">ISO 27001</th>
                  <th className="text-center py-4 px-3 font-semibold">HIPAA</th>
                  <th className="text-center py-4 px-3 font-semibold">GDPR</th>
                </tr>
              </thead>

              <tbody>
                {controlsMatrix.map((row, i) => (
                  <tr key={i} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                    <td className="py-3 px-4 font-semibold text-gray-900 whitespace-nowrap">{row.control}</td>
                    <td className="py-3 px-4 text-gray-600">{row.implementation}</td>

                    <td className="py-3 px-3 text-center">
                      <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-mono">{row.nist}</span>
                    </td>

                    <td className="py-3 px-3 text-center">
                      <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800 font-mono">{row.soc2}</span>
                    </td>

                    <td className="py-3 px-3 text-center">
                      <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 font-mono">{row.iso}</span>
                    </td>

                    <td className="py-3 px-3 text-center">
                      <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 font-mono">{row.hipaa}</span>
                    </td>

                    <td className="py-3 px-3 text-center">
                      <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-800 font-mono">{row.gdpr}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Defense in Depth Layers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <Shield className="mx-auto w-12 h-12 text-blue-600 mb-4" />
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Defense in Depth</h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Four independent security layers — each hardened separately so no single failure exposes data.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {securityLayers.map((layer, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 rounded-2xl p-8 border border-gray-200"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                    <layer.icon className="w-6 h-6 text-blue-600" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900">{layer.title}</h3>
                </div>

                <ul className="space-y-3">
                  {layer.controls.map((control, ci) => (
                    <li key={ci} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{control}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DevSecOps Pipeline */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center bg-green-500/20 text-green-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-green-500/30">
              <Cpu className="w-4 h-4 mr-2" />
              DevSecOps Pipeline
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Security Baked Into Every Deployment
            </h2>

            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Every code commit runs a full security gate. Vulnerable code cannot reach production.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {devSecOpsItems.map((item, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-slate-800/60 rounded-xl p-6 border border-slate-700 hover:border-green-500/40 transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-green-400" />
                  </div>

                  <span className="font-bold text-white font-mono">{item.tool}</span>
                </div>

                <p className="text-slate-400 text-sm">{item.purpose}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Due Diligence Policy */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-10 border border-blue-100"
          >
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Eye className="w-8 h-8 text-white" />
              </div>

              <div>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                  Client Due Diligence Policy
                </h2>

                <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                  We believe trust is earned through transparency, not paperwork. Our approach to due diligence
                  reflects a security-first philosophy — one that protects both our customers and ourselves from
                  the risks that conventional audit processes introduce.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />

                    <p className="text-gray-700">
                      <strong>What we offer:</strong>
                      {' '}
                      Prospective customers and partners are welcome to have
                      technical discussions with our engineering and security leadership. We will
                      walk you through our architecture, controls, and engineering practices in detail.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />

                    <p className="text-gray-700">
                      <strong>This page:</strong>
                      {' '}
                      Everything documented here is verifiable evidence of our security
                      posture — architecture decisions, encryption standards, DevSecOps tooling, and control mappings
                      — provided openly as a substitute for certificate theater.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />

                    <p className="text-gray-700">
                      <strong>What we do not grant:</strong>
                      {' '}
                      We do not provide system access, configuration files,
                      network credentials, or internal audit data to external parties under any circumstances.
                      This protects you as much as it protects us — your data lives in our platform, and we take
                      that responsibility seriously.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    style={{ backgroundColor: BOSSS_BLUE }}
                    className="px-8 py-3 text-white font-semibold rounded-xl"
                    onClick={() => onNavigateToTop('/contact')}
                  >
                    Schedule a Security Discussion
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>

                  <Button
                    variant="outline"
                    className="px-8 py-3 font-semibold rounded-xl"
                    style={{ borderColor: BOSSS_BLUE, color: BOSSS_BLUE }}
                    onClick={() => onNavigateToTop('/privacy-policy')}
                  >
                    View Privacy Policy
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}

export default SecurityPage;
